import React, { useState, useEffect } from 'react';
import { apiFetch, getApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../Messages';
import './WasteReviewDashboard.css';
import { formatAppDate } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const WasteReviewDashboard = ({ userRole = 'supervisor' }) => {
  const { t } = useTranslation('production');
  const { user } = useAuth();
  const { showSuccess, showError } = useMessages();
  const [pendingRecords, setPendingRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rollDetails, setRollDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [sortBy, setSortBy] = useState('created_at');

  useEffect(() => {
    loadPendingReviews();
  }, []);

  const loadPendingReviews = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(getApiUrl('/api/production-tasks/waste-records/pending-review'));
      if (response.ok) {
        const data = await response.json();
        setPendingRecords(data || []);
      }
    } catch (error) {
      console.error('Error loading pending reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRollDetails = async (taskId) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/rolls`));
      if (response.ok) {
        const data = await response.json();
        setRollDetails(data || []);
      }
    } catch (error) {
      console.error('Error loading roll details:', error);
    }
  };

  const handleSelectRecord = async (record) => {
    setSelectedRecord(record);
    setReviewNotes('');
    await loadRollDetails(record.task_id);
  };

  const handleApprove = async () => {
    if (!selectedRecord) return;

    try {
      const response = await apiFetch(
        getApiUrl(`/api/production-tasks/waste-records/${selectedRecord.waste_record_id}`),
        {
          method: 'PUT',
          body: JSON.stringify({
            review_status: 'APPROVED',
            reviewed_by: user?.user_id,
            review_notes: reviewNotes || 'Approved - waste within acceptable limits'
          })
        }
      );

      if (response.ok) {
        showSuccess(t('wasteReviewDashboard.toasts.approvedTitle'), t('wasteReviewDashboard.toasts.approvedMessage'));
        setSelectedRecord(null);
        setRollDetails([]);
        setReviewNotes('');
        await loadPendingReviews();
      } else {
        const err = await response.json().catch(() => ({}));
        showError(t('wasteReviewDashboard.toasts.approvalFailedTitle'), err.detail || t('wasteReviewDashboard.toasts.approveFailed'));
      }
    } catch (error) {
      console.error('Error approving record:', error);
      showError(t('common:ui.networkError'), t('wasteReviewDashboard.toasts.approveFailed'));
    }
  };

  const handleRequireAction = async () => {
    if (!selectedRecord) return;

    if (!reviewNotes.trim()) {
      showError(t('wasteReviewDashboard.toasts.notesRequiredTitle'), t('wasteReviewDashboard.toasts.notesRequiredMessage'));
      return;
    }

    try {
      const response = await apiFetch(
        getApiUrl(`/api/production-tasks/waste-records/${selectedRecord.waste_record_id}`),
        {
          method: 'PUT',
          body: JSON.stringify({
            review_status: 'REQUIRES_ACTION',
            reviewed_by: user?.user_id,
            review_notes: reviewNotes
          })
        }
      );

      if (response.ok) {
        showSuccess(t('wasteReviewDashboard.toasts.updatedTitle'), t('wasteReviewDashboard.toasts.updatedMessage'));
        setSelectedRecord(null);
        setRollDetails([]);
        setReviewNotes('');
        await loadPendingReviews();
      } else {
        const err = await response.json().catch(() => ({}));
        showError(t('wasteReviewDashboard.toasts.updateFailedTitle'), err.detail || t('wasteReviewDashboard.toasts.updateFailed'));
      }
    } catch (error) {
      console.error('Error updating record:', error);
      showError(t('common:ui.networkError'), t('wasteReviewDashboard.toasts.updateFailed'));
    }
  };

  const getWasteColor = (percentage) => {
    if (!percentage) return '#6c757d';
    if (percentage < 3) return '#28a745';
    if (percentage < 5) return '#ffc107';
    return '#dc3545';
  };

  const getCategoryColor = (category) => {
    const colors = {
      SETUP: '#ffc107',
      QUALITY_DEFECT: '#dc3545',
      TRIM_SCRAP: '#6c757d',
      OTHER: '#17a2b8'
    };
    return colors[category] || '#6c757d';
  };

  const getCategoryLabel = (category) =>
    t(`wasteReviewDashboard.categories.${category}`, { defaultValue: category.replace('_', ' ') });

  const filteredRecords = pendingRecords
    .filter(record => {
      if (filterCategory === 'ALL') return true;
      return record.primary_waste_category === filterCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'created_at') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'waste_percentage') {
        return (b.waste_percentage || 0) - (a.waste_percentage || 0);
      } else if (sortBy === 'waste_weight') {
        return (b.total_waste_weight_kg || 0) - (a.total_waste_weight_kg || 0);
      }
      return 0;
    });

  return (
    <div className="waste-review-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>🔍 {t('wasteReviewDashboard.title')}</h2>
          <p className="subtitle">{t('wasteReviewDashboard.subtitle')}</p>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-label">{t('wasteReviewDashboard.pendingReviews')}</div>
            <div className="stat-value">{pendingRecords.length}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Filters and Controls */}
        <div className="controls-panel">
          <div className="filter-group">
            <label>{t('wasteReviewDashboard.filterByCategory')}:</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-select"
            >
              <option value="ALL">{t('wasteReviewDashboard.allCategories')}</option>
              <option value="SETUP">{t('wasteReviewDashboard.filterOptions.setup')}</option>
              <option value="QUALITY_DEFECT">{t('wasteReviewDashboard.filterOptions.qualityDefect')}</option>
              <option value="TRIM_SCRAP">{t('wasteReviewDashboard.filterOptions.trimScrap')}</option>
              <option value="OTHER">{t('wasteReviewDashboard.filterOptions.other')}</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{t('wasteReviewDashboard.sortBy')}:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="created_at">{t('wasteReviewDashboard.sort.dateNewest')}</option>
              <option value="waste_percentage">{t('wasteReviewDashboard.sort.wastePercentHighest')}</option>
              <option value="waste_weight">{t('wasteReviewDashboard.sort.wasteWeightHighest')}</option>
            </select>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={loadPendingReviews}
          >
            🔄 {t('common:ui.refresh')}
          </button>
        </div>

        <div className="dashboard-layout">
          {/* Records List */}
          <div className="records-panel">
            <h3>{t('wasteReviewDashboard.pendingReviews')} ({filteredRecords.length})</h3>
            {loading ? (
              <div className="loading-state">{t('common:ui.loading')}</div>
            ) : filteredRecords.length === 0 ? (
              <div className="empty-state">
                <p>✅ {t('wasteReviewDashboard.emptyTitle')}</p>
                <p className="empty-subtitle">{t('wasteReviewDashboard.emptySubtitle')}</p>
              </div>
            ) : (
              <div className="records-list">
                {filteredRecords.map((record) => (
                  <div
                    key={record.waste_record_id}
                    className={`record-card ${selectedRecord?.waste_record_id === record.waste_record_id ? 'selected' : ''}`}
                    onClick={() => handleSelectRecord(record)}
                  >
                    <div className="record-header">
                      <div className="record-title">
                        <strong>{record.task_code}</strong>
                        <span className="task-name">{record.task_name}</span>
                      </div>
                      <div
                        className="waste-badge"
                        style={{
                          backgroundColor: getWasteColor(record.waste_percentage),
                          color: 'white'
                        }}
                      >
                        {record.waste_percentage ? `${record.waste_percentage.toFixed(1)}%` : t('common:ui.notAvailable')}
                      </div>
                    </div>
                    <div className="record-details">
                      <div className="detail-row">
                        <span className="detail-label">{t('wasteReviewDashboard.waste')}:</span>
                        <span className="detail-value">
                          {record.total_waste_weight_kg ? `${record.total_waste_weight_kg.toFixed(2)} kg` : t('common:ui.notAvailable')}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{t('wasteReviewDashboard.input')}:</span>
                        <span className="detail-value">
                          {record.total_input_weight_kg ? `${record.total_input_weight_kg.toFixed(2)} kg` : t('common:ui.notAvailable')}
                        </span>
                      </div>
                      {record.primary_waste_category && (
                        <div className="detail-row">
                          <span className="detail-label">{t('wasteReviewDashboard.category')}:</span>
                          <span
                            className="category-tag"
                            style={{
                              backgroundColor: getCategoryColor(record.primary_waste_category),
                              color: 'white'
                            }}
                          >
                            {getCategoryLabel(record.primary_waste_category)}
                          </span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">{t('wasteReviewDashboard.created')}:</span>
                        <span className="detail-value">
                          {formatAppDate(record.created_at)} {formatAppDate(record.created_at, 'time')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Review Details Panel */}
          <div className="review-panel">
            {selectedRecord ? (
              <>
                <div className="review-header">
                  <h3>{t('wasteReviewDashboard.reviewDetails')}</h3>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setSelectedRecord(null);
                      setRollDetails([]);
                      setReviewNotes('');
                    }}
                  >
                    ✖
                  </button>
                </div>

                {/* Task Information */}
                <div className="info-section">
                  <h4>{t('wasteReviewDashboard.taskInformation')}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>{t('wasteReviewDashboard.taskCode')}:</label>
                      <span>{selectedRecord.task_code}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('wasteReviewDashboard.taskName')}:</label>
                      <span>{selectedRecord.task_name}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('wasteReviewDashboard.productionOrder')}:</label>
                      <span>{selectedRecord.po_number || '—'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('common:ui.customer')}:</label>
                      <span>{selectedRecord.customer_name || '—'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('common:ui.product')}:</label>
                      <span>{selectedRecord.product_name || '—'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('wasteReviewDashboard.created')}:</label>
                      <span>{formatAppDate(selectedRecord.created_at, 'datetime')}</span>
                    </div>
                  </div>
                </div>

                {/* Waste Summary */}
                <div className="info-section">
                  <h4>{t('wasteReviewDashboard.wasteSummary')}</h4>
                  <div className="summary-cards">
                    <div className="summary-card">
                      <div className="summary-label">{t('wasteReviewDashboard.totalInput')}</div>
                      <div className="summary-value">
                        {selectedRecord.total_input_weight_kg?.toFixed(2) || t('common:ui.notAvailable')} kg
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">{t('wasteReviewDashboard.totalWaste')}</div>
                      <div className="summary-value waste">
                        {selectedRecord.total_waste_weight_kg?.toFixed(2) || t('common:ui.notAvailable')} kg
                      </div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">{t('wasteReviewDashboard.wastePercent')}</div>
                      <div
                        className="summary-value"
                        style={{ color: getWasteColor(selectedRecord.waste_percentage) }}
                      >
                        {selectedRecord.waste_percentage?.toFixed(1) || t('common:ui.notAvailable')}%
                      </div>
                    </div>
                    {selectedRecord.primary_waste_category && (
                      <div className="summary-card">
                        <div className="summary-label">{t('wasteReviewDashboard.primaryCategory')}</div>
                        <div className="summary-value">
                          <span
                            className="category-tag"
                            style={{
                              backgroundColor: getCategoryColor(selectedRecord.primary_waste_category)
                            }}
                          >
                            {getCategoryLabel(selectedRecord.primary_waste_category)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedRecord.waste_notes && (
                    <div className="notes-box">
                      <strong>{t('common:ui.notes')}:</strong>
                      <p>{selectedRecord.waste_notes}</p>
                    </div>
                  )}
                </div>

                {/* Roll Consumption Details */}
                {rollDetails.length > 0 && (
                  <div className="info-section">
                    <h4>{t('wasteReviewDashboard.rollConsumptionDetails', { count: rollDetails.length })}</h4>
                    <div className="rolls-table-container">
                      <table className="rolls-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t('wasteReviewDashboard.columns.barcode')}</th>
                            <th>{t('wasteReviewDashboard.columns.initialKg')}</th>
                            <th>{t('wasteReviewDashboard.columns.finalKg')}</th>
                            <th>{t('wasteReviewDashboard.columns.wasteKg')}</th>
                            <th>{t('wasteReviewDashboard.columns.meters')}</th>
                            <th>{t('wasteReviewDashboard.category')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rollDetails.map((roll) => (
                            <tr key={roll.roll_consumption_id}>
                              <td>#{roll.roll_sequence}</td>
                              <td>{roll.roll_barcode || '-'}</td>
                              <td>{roll.initial_weight_kg}</td>
                              <td>{roll.final_weight_kg}</td>
                              <td className="waste-cell">{roll.waste_weight_kg?.toFixed(3)}</td>
                              <td>{roll.final_meters || '-'}</td>
                              <td>
                                {roll.waste_category ? (
                                  <span
                                    className="category-tag small"
                                    style={{
                                      backgroundColor: getCategoryColor(roll.waste_category)
                                    }}
                                  >
                                    {getCategoryLabel(roll.waste_category)}
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Review Actions */}
                <div className="review-actions-section">
                  <h4>{t('wasteReviewDashboard.reviewActions')}</h4>
                  <div className="review-notes-field">
                    <label>{t('wasteReviewDashboard.reviewNotes')}:</label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder={t('wasteReviewDashboard.reviewNotesPlaceholder')}
                      rows="4"
                      className="review-textarea"
                    />
                  </div>
                  <div className="action-buttons">
                    <button
                      className="btn btn-success btn-lg"
                      onClick={handleApprove}
                    >
                      ✅ {t('wasteReviewDashboard.approve')}
                    </button>
                    <button
                      className="btn btn-warning btn-lg"
                      onClick={handleRequireAction}
                    >
                      ⚠️ {t('wasteReviewDashboard.requireAction')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>👈 {t('wasteReviewDashboard.selectRecord')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WasteReviewDashboard;
